import { apiServiceWithFallback } from './api.service';

export interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  avatar?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
  accountId?: string;
  account?: {
    id: string;
    name: string;
    createdAt: string;
    updatedAt: string;
  };
}

export interface CreateUserDto {
  name: string;
  email: string;
  password: string;
  role: string;
  accountId: string;
  avatar?: string;
}

export interface UpdateUserDto {
  name?: string;
  email?: string;
  role?: string;
  isActive?: boolean;
  avatar?: string;
}

export interface UserResponseDto {
  id: string;
  name: string;
  email: string;
  role: string;
  avatar?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  accountId: string;
}

class UsersService {
  private normalizeUserData(user: any): User {
    return {
      id: user.id,
      name: user.name,
      email: user.email,
      role: user.role,
      avatar: user.avatar,
      isActive: user.isActive,
      createdAt: user.createdAt,
      updatedAt: user.updatedAt,
      accountId: user.accountId || user.account?.id,
      account: user.account
    };
  }

  async getAllUsers(): Promise<User[]> {
    try {
      console.log('🔍 getAllUsers: Iniciando chamada...');
      const response = await apiServiceWithFallback.get('/api/users');
      console.log('🔍 getAllUsers: Response recebida:', response);
      
      // Estrutura real: response.data = { success: true, data: [...], count: 19 }
      // Estrutura mock: response.data = { data: [...], success: true }
      
      let usersArray: any[] = [];
      
      // Verificar se é estrutura da API real
      if (response.data && typeof response.data === 'object' && 'success' in response.data && 'data' in response.data && response.data.success && Array.isArray(response.data.data)) {
        console.log('✅ getAllUsers: Estrutura API real detectada, processando', response.data.data.length, 'usuários');
        usersArray = response.data.data;
      }
      // Verificar se é estrutura do mock
      else if (response.data && typeof response.data === 'object' && 'data' in response.data && Array.isArray(response.data.data)) {
        console.log('✅ getAllUsers: Estrutura mock detectada, processando', response.data.data.length, 'usuários');
        usersArray = response.data.data;
      }
      // Verificar se é array direto (fallback)
      else if (Array.isArray(response.data)) {
        console.log('✅ getAllUsers: Array direto detectado, processando', response.data.length, 'usuários');
        usersArray = response.data;
      }
      else {
        console.error('❌ getAllUsers: Estrutura não reconhecida:', response);
        console.error('❌ getAllUsers: response.data:', response.data);
        return [];
      }
      
      // Normalizar cada usuário para garantir compatibilidade
      const users = usersArray.map(user => this.normalizeUserData(user));
      
      console.log('✅ getAllUsers: Retornando array com', users.length, 'usuários');
      return users;
    } catch (error) {
      console.error('❌ getAllUsers: Erro na chamada:', error);
      throw error;
    }
  }

  async getUsersByAccount(accountId: string): Promise<User[]> {
    try {
      const response = await apiServiceWithFallback.get(`/api/accounts/${accountId}/users`);
      
      let usersArray: any[] = [];
      
      // Verificar se é estrutura da API real
      if (response.data && typeof response.data === 'object' && 'success' in response.data && 'data' in response.data && response.data.success && Array.isArray(response.data.data)) {
        console.log('✅ getUsersByAccount: Estrutura API real detectada, processando', response.data.data.length, 'usuários');
        usersArray = response.data.data;
      }
      // Verificar se é estrutura do mock
      else if (response.data && typeof response.data === 'object' && 'data' in response.data && Array.isArray(response.data.data)) {
        console.log('✅ getUsersByAccount: Estrutura mock detectada, processando', response.data.data.length, 'usuários');
        usersArray = response.data.data;
      }
      // Verificar se é array direto (fallback)
      else if (Array.isArray(response.data)) {
        console.log('✅ getUsersByAccount: Array direto detectado, processando', response.data.length, 'usuários');
        usersArray = response.data;
      }
      else {
        console.error('❌ getUsersByAccount: Estrutura não reconhecida:', response);
        return [];
      }
      
      // Normalizar cada usuário para garantir compatibilidade
      const users = usersArray.map(user => this.normalizeUserData(user));
      
      return users;
    } catch (error) {
      console.error('Error fetching users by account:', error);
      throw error;
    }
  }

  async getUserById(id: string): Promise<User> {
    try {
      const response = await apiServiceWithFallback.get(`/api/users/${id}`);
      
      // Para usuário individual, a estrutura pode variar
      let userData: any = null;
      
      if (response.data && typeof response.data === 'object' && 'success' in response.data && 'data' in response.data && response.data.success) {
        userData = response.data.data;
      } else if (response.data && typeof response.data === 'object' && 'id' in response.data) {
        userData = response.data;
      } else {
        throw new Error('User not found');
      }
      
      return this.normalizeUserData(userData);
    } catch (error) {
      console.error('Error fetching user:', error);
      throw error;
    }
  }

  async createUser(userData: CreateUserDto): Promise<UserResponseDto> {
    try {
      const response = await apiServiceWithFallback.post('/api/users', userData);
      return response.data as UserResponseDto;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  async updateUser(id: string, userData: UpdateUserDto): Promise<UserResponseDto> {
    try {
      const response = await apiServiceWithFallback.put(`/api/users/${id}`, userData);
      return response.data as UserResponseDto;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  async deleteUser(id: string): Promise<void> {
    try {
      await apiServiceWithFallback.delete(`/api/users/${id}`);
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  async deactivateUser(id: string): Promise<UserResponseDto> {
    try {
      const response = await apiServiceWithFallback.patch(`/api/users/${id}/deactivate`);
      return response.data as UserResponseDto;
    } catch (error) {
      console.error('Error deactivating user:', error);
      throw error;
    }
  }

  async activateUser(id: string): Promise<UserResponseDto> {
    try {
      const response = await apiServiceWithFallback.patch(`/api/users/${id}/activate`);
      return response.data as UserResponseDto;
    } catch (error) {
      console.error('Error activating user:', error);
      throw error;
    }
  }
}

export const usersService = new UsersService(); 